import * as yup from 'yup';

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  useAddNewCVMutation,
  useGetCandidateCvsQuery,
} from "../../lib/cvsSlice";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import Sidebar from "../../components/Sidebar";
import TokenExpiredModal from "../../components/TokenExpiredModal";
import UploadPDF from "../../components/UploadCVExtractDataWithAiApp";
import api from "../../api/axiosInstance";
import { primaryRoles } from "../../config";
import {useForm} from "react-hook-form";
import { useNavigate } from "react-router";
import { useProfile } from "../../providers/ProfileProvider";
import { yupResolver } from "@hookform/resolvers/yup";

const cvHeaderSchema = yup.object().shape({
  mainRole: yup.string().required("CV Role is required"),
});

const ActionButtons = ({ cv, navigate }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ display: 'flex', gap: '12px' }}>
      <IconButton size="small" sx={{ color: theme.palette.text.secondary }} title="Download CV">
        <DownloadIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" sx={{ color: theme.palette.text.secondary }} title="Print CV">
        <PrintIcon fontSize="small" />
      </IconButton>
      <IconButton 
        size="small" 
        sx={{ color: theme.palette.text.secondary }}
        onClick={() => navigate(`/cvdetails/${cv._id}`)}
        title="Edit CV"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" sx={{ color: theme.palette.text.secondary }} title="Delete CV">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const ActionCard = ({ icon: Icon, title, description, buttonText, onClick, progress, iconColor, hoverable = false }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        width: '100%',
        height: '152px',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: theme.palette.background.paper,
        transition: hoverable ? 'all 0.2s ease' : 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        '&:hover': hoverable ? {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4], 
        } : {}
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box
          sx={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ color: theme.palette.common.white, fontSize: '20px' }} />
        </Box>
        <Typography variant="h6">{title}</Typography>
      </Box>
      
      <Typography variant="body2" sx={{ marginBottom: '16px' }}>
        {description}
      </Typography>
      
      {progress !== undefined ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              flex: 1, 
              height: '8px', 
              borderRadius: '4px',
              backgroundColor: theme.palette.grey[100],
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.text.primary
              }
            }} 
          />
          <Button
            variant="outlined"
            sx={{
              color: theme.palette.text.primary,
              borderColor: theme.palette.text.primary,
              height: '40px',
              '&:hover': {
                borderColor: theme.palette.grey[500],
                backgroundColor: theme.palette.grey[25]
              }
            }}
            startIcon={<Icon />}
            onClick={onClick}
          >
            {buttonText}
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={onClick}
          sx={{
            width: '100%', 
            height: '32px',
            padding: '4px 10px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '8px',
            alignSelf: 'flex-start',
            transition: 'none',
            animationDuration: '0ms',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            }
          }}
          startIcon={<Icon />}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  );
};

const CreateCVDialog = ({ open, onClose, onSubmit, allCvs, errors, control }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [copyCvId, setCopyCvId] = useState(null);
  const [createFromPersonal, setCreateFromPersonal] = useState(false);
  const theme = useTheme();

  const roles = [...primaryRoles];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role !== 'Other') {
      setCustomRole('');
    }
  };

  const handleClose = () => {
    setSelectedRole('');
    setCustomRole('');
    setCopyCvId(null);
    setCreateFromPersonal(false);
    onClose();
  };

  const handleCreate = () => {
    const roleValue = selectedRole === 'Other' ? customRole : selectedRole;
    onSubmit({ mainRole: roleValue, copyCvId, createFromPersonal });
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: theme.typography.fontFamily
    }}>
      <div style={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          borderBottom: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
            <h2 style={{ 
              margin: 0, 
              fontSize: '24px', 
              fontWeight: '600',
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamily
            }}>
              Create New CV
            </h2>
          </div>
          <button 
            onClick={handleClose}
            style={{
              backgroundColor: theme.palette.grey[25],
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = theme.palette.grey[50]}
            onMouseLeave={(e) => e.target.style.backgroundColor = theme.palette.grey[25]}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div style={{ padding: '0 24px 24px 24px' }}>
          <div style={{ marginBottom: '32px' }}>
            <p style={{ 
              margin: '0 0 16px 0', 
              fontSize: '16px', 
              fontWeight: '500',
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily
            }}>
              Select Role for CV.
            </p>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px',
              marginBottom: '24px'
            }}>
              {roles.map((role) => (
                <button
                  key={role}
                  onClick={() => handleRoleSelect(role)}
                  style={{
                    border: selectedRole === role ? 'none' : `1px solid ${theme.palette.grey[50]}`,
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: selectedRole === role ? theme.palette.background.neutral : 'transparent',
                    color: selectedRole === role ? theme.palette.text.primary : theme.palette.text.secondary,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRole !== role) {
                      e.target.style.backgroundColor = theme.palette.grey[25];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRole !== role) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {role}
                </button>
              ))}
            </div>

            {selectedRole === 'Other' && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: theme.palette.text.secondary,
                  fontFamily: theme.typography.fontFamily
                }}>
                  Other Role
                </label>
                <input
                  type="text"
                  placeholder="Enter Role"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.palette.grey[50]}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: theme.palette.grey[25],
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = theme.palette.primary.main}
                  onBlur={(e) => e.target.style.borderColor = theme.palette.grey[50]}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setCopyCvId(copyCvId !== null ? null : "")}
              style={{
                width: '100%',
                height: '48px',
                marginBottom: '12px',
                borderRadius: '8px',
                backgroundColor: theme.palette.background.neutral,
                border: copyCvId !== null ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.grey[50]}`,
                color: theme.palette.text.secondary,
                fontWeight: '600',
                fontSize: '14px',
                fontFamily: theme.typography.fontFamily,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                if (copyCvId === null) {
                  e.target.style.borderColor = theme.palette.grey[300];
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (copyCvId === null) {
                  e.target.style.borderColor = theme.palette.grey[50];
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              DUPLICATE AN EXISTING CV
            </button>

            {copyCvId !== null && (
              <div style={{ marginBottom: '12px' }}>
                <select
                  value={copyCvId}
                  onChange={(e) => setCopyCvId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.palette.grey[50]}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: theme.palette.background.paper,
                    outline: 'none'
                  }}
                >
                  <option value="">Select CV to copy</option>
                  {allCvs.map((cv) => (
                    <option key={cv._id} value={cv._id}>
                      {cv.mainRole}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <button
              onClick={() => setCreateFromPersonal(!createFromPersonal)}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: theme.palette.background.neutral,
                border: createFromPersonal ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.grey[50]}`,
                color: theme.palette.text.secondary,
                fontWeight: '600',
                fontSize: '14px',
                fontFamily: theme.typography.fontFamily,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                if (!createFromPersonal) {
                  e.target.style.borderColor = theme.palette.grey[300];
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!createFromPersonal) {
                  e.target.style.borderColor = theme.palette.grey[50];
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              CREATE A "CLEAN CV" FROM ZERO
            </button>

            {createFromPersonal && (
              <div style={{ marginTop: '16px' }}>
                <UploadPDF handleClose={handleClose} />
              </div>
            )}
            
            <p style={{ 
              margin: '16px 0 0 0',
              color: theme.palette.grey[500],
              fontSize: '13px',
              fontFamily: theme.typography.fontFamily,
              lineHeight: '1.4'
            }}>
              Starting from a blank CV will require you to enter your information again, even if you've already created a CV with us.
            </p>
          </div>

          <div style={{ 
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={handleClose}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                textTransform: 'uppercase',
                fontWeight: '600',
                fontFamily: theme.typography.fontFamily,
                border: `1px solid ${theme.palette.grey[50]}`,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = theme.palette.grey[300];
                e.target.style.backgroundColor = theme.palette.grey[25];
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = theme.palette.grey[50];
                e.target.style.backgroundColor = theme.palette.background.paper;
              }}
            >
              CANCEL
            </button>
            <button
              onClick={handleCreate}
              disabled={!selectedRole || (selectedRole === 'Other' && !customRole.trim())}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                backgroundColor: (!selectedRole || (selectedRole === 'Other' && !customRole.trim())) ? theme.palette.grey[50] : theme.palette.primary.main,
                color: (!selectedRole || (selectedRole === 'Other' && !customRole.trim())) ? theme.palette.grey[500] : theme.palette.common.white,
                border: 'none',
                textTransform: 'uppercase',
                fontWeight: '600',
                fontFamily: theme.typography.fontFamily,
                cursor: (!selectedRole || (selectedRole === 'Other' && !customRole.trim())) ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedRole && (selectedRole !== 'Other' || customRole.trim())) {
                  e.target.style.backgroundColor = theme.palette.primary.dark;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedRole && (selectedRole !== 'Other' || customRole.trim())) {
                  e.target.style.backgroundColor = theme.palette.primary.main;
                }
              }}
            >
              CREATE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const generateCvId = (index) => {
  const baseNumber = 25000000;
  const uniqueId = baseNumber + index + 1;
  return `NC-${uniqueId}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-CA'); 
};

const getTimeAgo = (dateString) => {
  if (!dateString) return 'N/A';
  
  const now = new Date();
  const updatedDate = new Date(dateString);
  const diffInMs = now - updatedDate;
  
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInMonths / 12);
  
  if (diffInYears > 0) {
    const remainingMonths = diffInMonths % 12;
    if (remainingMonths > 0) {
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''} ago`;
    }
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  }
  
  if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  }
  
  if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  
  return 'Today';
};

const DashBoard = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const profile = useProfile();
  const navigate = useNavigate();

  // ✅ Use Redux query with profile ID
  const { 
    data: cvsData, 
    isLoading, 
    isSuccess,
    isError,
    error 
  } = useGetCandidateCvsQuery(profile?._id, {
    skip: !profile?._id
  });

  const [addNewCV] = useAddNewCVMutation();
  const [allCvs, setAllCvs] = useState([]);
  const [allCvTitles, setAllCvTitles] = useState([]);
  const [tokenExpiredModalOpen, setTokenExpiredModalOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [presentedToCustomer, setPresentedToCustomer] = useState("All");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    const existingLink = document.querySelector('link[href*="Readex+Pro"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Readex+Pro:wght@200;300;400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    
    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, []);

  // ✅ Sync Redux data to local state
  useEffect(() => {
    if (isSuccess && cvsData) {
      console.log('CVs Data from Redux:', cvsData);
      const cvArray = Object.values(cvsData.entities || {});
      setAllCvs(cvArray);
      setAllCvTitles(cvArray.map(cv => cv.mainRole));
    }
  }, [isSuccess, cvsData]);

  // ✅ Handle errors
  useEffect(() => {
    if (isError) {
      console.error('Error fetching CVs:', error);
      if (error?.status === 401) {
        setTokenExpiredModalOpen(true);
      }
    }
  }, [isError, error]);


  useEffect(() => {
  // Only redirect if profile is fully loaded
  if (profile && profile._id && !profile.secondaryEmail && !isLoading) {
    navigate('/onboarding');
  }
}, [profile, navigate, isLoading]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvHeaderSchema),
    defaultValues: {
      mainRole: "",
    },
  });

  const submitResults = async (formData) => {
    const { mainRole, copyCvId: cvIdToCopy, createFromPersonal: fromPersonal } = formData;

    let cvData = {};

    if (cvIdToCopy) {
      try {
        const response = await api.get(`cv/${cvIdToCopy}`);

        cvData = {
          languages: response.data.languages || [{}],
          educations: response.data.educations || [{}],
          skills: response.data.skills || [{}],
          achievements: response.data.achievements || [],
          certifications: response.data.certifications || [{}],
          projects: response.data.projects || [{}],
          courses: response.data.courses || [{}],
          gitRepo: response.data.gitRepo || "",
          portfolio: response.data.portfolio || "",
          summary: response.data.summary || "",
          techSkills: response.data.techSkills || [{}],
          techExperiences: response.data.techExperiences || [{}],
        };
      } catch (error) {
        console.error(error);
      }
    }

    const requestData = {
      mainRole,
      ...(cvIdToCopy ? cvData : {}),
      ...formData,
    };

    try {
      const { data: responseData } = await addNewCV(requestData);
      setOpen(false);
      reset();
      navigate(`/cvdetails/${responseData._id}`);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const filteredData = allCvs.filter(cv => {
    const matchesSearch = cv.mainRole && cv.mainRole.toLowerCase().includes(searchName.toLowerCase());
    const matchesFilter = presentedToCustomer === "All" || presentedToCustomer === "No";
    return matchesSearch && matchesFilter;
  });

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = Math.min(endIndex, totalItems);

  return (
    <div>
      <TokenExpiredModal
        isOpen={tokenExpiredModalOpen}
        onClose={() => setTokenExpiredModalOpen(false)}
      />

      <Sidebar currentPath="/" />

      <div 
        style={{
          position: 'fixed',
          top: '65px',
          left: '258px',
          width: '85vw',
          height: '95vh',
          overflow: 'auto',
          padding: '16px 24px',
          boxSizing: 'border-box',
          background: theme.palette.background.neutral
        }}
      >
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <CircularProgress />
          </div>
        )}
        
        <Typography variant="h4" sx={{ marginBottom: '8px' }}>
          CVs
        </Typography>

        <Typography variant="body2" sx={{ marginBottom: '24px', lineHeight: 1.4 }}>
          Manage your CVs from here.
        </Typography>
        
        <div style={{ position: 'relative', height: '152px', marginBottom: '24px' }}>
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: 'calc((100vw - 346px) / 2)',
            height: '152px' 
          }}>
            <ActionCard
              icon={AddCircleIcon}
              title="Create New CV"
              description="Start building a new CV from scratch"
              buttonText="Create CV"
              onClick={handleClickOpen}
              iconColor={theme.palette.primary.main}
              hoverable
            />
          </div>

          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 'calc((100vw - 346px) / 2 + 16px)', 
            width: 'calc((100vw - 346px) / 2)',
            height: '152px' 
          }}>
            <ActionCard
              icon={EditIcon}
              title="Complete Your CV"
              description='Your "Marketing Manager" CV is 75% complete'
              buttonText="Complete"
              progress={75}
              iconColor="#7b68ee"
            />
          </div>
        </div>

        <Box sx={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '24px',
          alignItems: 'center'
        }}>
        </Box>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: `1px solid ${theme.palette.grey[50]}`
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CV Role</TableCell>
                <TableCell>CV ID No</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>CV Quality Index</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Presented to Customer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((cv, index) => {
                const actualIndex = startIndex + index;
                
                return (
                  <TableRow key={cv._id || index} sx={{ height: '48px' }}>
                    <TableCell>{cv.mainRole}</TableCell>
                    <TableCell>{cv.cvId || generateCvId(actualIndex)}</TableCell>
                    <TableCell>{formatDate(cv.createdAt || cv.createdDate)}</TableCell>
                    <TableCell>{getTimeAgo(cv.updatedAt || cv.lastUpdated)}</TableCell>
                    <TableCell>{cv.qualityIndex || 85}%</TableCell>
                    <TableCell>
                      <ActionButtons cv={cv} navigate={navigate} />
                    </TableCell>
                    <TableCell>No</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          marginTop: '16px',
          height: '40px',
          gap: '16px'
        }}>
          <Typography variant="body2">
            Rows per page: {rowsPerPage}
          </Typography>
          <Typography variant="body2">
            {displayStart}-{displayEnd} of {totalItems}
          </Typography>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            size="small"
          />
        </Box>
      </div>

      <CreateCVDialog
        open={open}
        onClose={handleClose}
        onSubmit={submitResults}
        allCvs={allCvs}
        errors={errors}
        control={control}
      />
    </div>
  );
};

export default DashBoard;